import React from 'react'

import * as gs from '../../../app/gStyles'
import PageReport, {
  PageReportProps,
  borderHeight,
  horizontalPad,
  pageHeight,
  pageWidth,
  titleHeight,
  verticalPad,
} from '../PageReport'

/**
 * @typedef {import('../../../common').Customer} Customer
 */

export interface ReportPagesProps {
  children: React.ReactNode
  companyBorder?: boolean
  hPad?: PageReportProps['hPad']
  /**
   * @see PageReportProps['isPDF']
   */
  isPDF: PageReportProps['isPDF']
  /**
   * @see PageReportProps['noAlignCenter']
   */
  noAlignCenter?: PageReportProps['noAlignCenter']
  /**
   * @see PageReportProps['noPDF']
   */
  noPDF?: PageReportProps['noPDF']
  /**
   * @see Customer['solarCompany']
   */
  state: string
  title?: string
}

/**
 * Helps split content into several `<PageReport />`s, each one actually
 * reflecting A4 paper dimensions, so as to match what will be generated for the
 * PDF. This component will not handle individual items that are more than one
 * page tall. Providing just one `<div />` direct descendant to this component
 * will not allow it to break the content into pages, the closest to leaf nodes
 * are provided the better, avoid div-itis. Use `align-self: flex-start` to
 * align paragraphs to the left. If you have a <ContractPages /> component, do
 * not do `<ReportPages><ContractPages /></ReportPages>` but use <ReportPages>
 * inside <ContractPages /> as <ReportPages> won't be able to look at
 * <ContractPages />'s rendered children to do the splitting math. Use a
 * wrapping div with `display: 'flow-root'` to group together elements that
 * should not be split across pages.
 */
export default React.memo<ReportPagesProps>(function ReportPages({
  children,
  companyBorder,
  hPad,
  isPDF,
  noAlignCenter,
  noPDF,
  state,
  title,
}) {
  const [layout, setLayout] = React.useState<React.ReactNode[][] | null>(null)
  const childArray = React.useMemo(
    () => React.Children.toArray(children),
    [children],
  )
  const refs = React.useRef<Array<HTMLElement | null>>([])

  React.useLayoutEffect(() => {
    const heights = refs.current.map(
      (el) => el?.getBoundingClientRect().height ?? 0,
    )

    let chunkHeight = basePageHeight
    if (companyBorder) chunkHeight -= borderHeight * 2
    if (title) chunkHeight -= titleHeight

    const chunks = chunkByHeight(
      childArray.map((value, i) => ({ height: heights[i] || 0, value })),
      chunkHeight,
    )

    setLayout(chunks)
  }, [childArray, companyBorder, title])

  return (
    <>
      <div
        style={{
          left: -9999,
          pointerEvents: 'none',
          position: 'absolute',
          top: -9999,
          visibility: 'hidden',
          width: hPad ? pageWidth - horizontalPad * 2 : pageWidth,
        }}
      >
        {childArray.map((child, i) => (
          <div
            key={i}
            ref={(el) => void (refs.current[i] = el)}
            style={gs.flowRoot}
          >
            {child}
          </div>
        ))}
      </div>

      {layout?.map((page, i, { length }) => (
        <PageReport
          company={state}
          companyBorder={companyBorder}
          hPad={hPad}
          isPDF={isPDF}
          key={i}
          noAlignCenter={noAlignCenter}
          noPDF={noPDF}
          title={title ? `${title} (${i + 1}/${length})` : undefined}
        >
          {page}
        </PageReport>
      ))}
    </>
  )
})

interface HeightItem<T> {
  height: number
  value: T
}
function chunkByHeight<T>(items: HeightItem<T>[], chunkHeight: number): T[][] {
  const result: T[][] = []
  let current: T[] = []
  let used = 0

  for (const item of items) {
    const h = item.height ?? 0

    // If current has something and adding would overflow -> start a new page
    if (current.length > 0 && used + h > chunkHeight) {
      result.push(current)
      current = []
      used = 0
    }

    // If the item itself is taller than a page:
    // since you don't want splitting, it must live alone in its own chunk.
    if (h > chunkHeight) {
      if (current.length > 0) result.push(current)
      result.push([item.value])
      current = []
      used = 0
      continue
    }

    current.push(item.value)
    used += h
  }

  if (current.length > 0) result.push(current)
  return result
}

const basePageHeight = pageHeight - verticalPad * 2

export { borderHeight, horizontalPad, pageHeight, pageWidth, verticalPad }
